import { useCallback, useEffect, useState } from 'react';
import {
  ArrowBack,
  DragHandle,
  ExpandLess,
  ExpandMore,
  Search,
} from '@mui/icons-material';
import {
  AppBar,
  type AppBarProps,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { headerHeight } from './constants/header-height';
import {
  CategoriesMenu,
  HeaderSkeleton,
  MainLogo,
  MobileMenu,
  SearchInput,
  UserMenu,
} from '@/components/layout/header/components';
import { menuItems } from '@/components/layout/header/constants/menu-items';
import type { MenuItem } from '@/components/layout/header/interfaces';
import { filterMenu } from '@/components/layout/header/utils';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { useNavigate, usePathname } from '@/hooks/use-navigation';

const StyledAppBar = styled(AppBar)<AppBarProps>(({ theme }) => ({
  boxShadow: 'none',
  minHeight: headerHeight,
  display: 'flex',
  justifyContent: 'center',
  '&.MuiPaper-root': { backgroundColor: theme.palette.background.paper },
  zIndex: 10,
}));

export const Header = () => {
  const path = usePathname();
  const navigate = useNavigate();
  const { bootstrap, isLoading: isLoadingBootstrap } = useBootstrapData();
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const visibleMenu = filterMenu({ bootstrap, items: menuItems });

  const goToItemPath = useCallback(
    (item: MenuItem) => {
      if (!item.path) return;
      const p = typeof item.path === 'string' ? item.path : undefined;
      if (p) navigate(p);
    },
    [navigate]
  );

  const onMenuItemClick = useCallback(
    (item: MenuItem) => {
      if (item.onClick) return item.onClick();

      const courses = item.label === 'Cursos';

      if (courses) {
        setCategoryMenuOpen((open) => !open);
      } else {
        setCategoryMenuOpen(false);
        goToItemPath(item);
      }
    },
    [goToItemPath]
  );

  const handleMobileClick = (item: MenuItem) => {
    onMenuItemClick(item);
    setMobileOpen(false);
  };

  useEffect(() => setCategoryMenuOpen(false), [path]);

  return (
    <>
      <StyledAppBar>
        <Container maxWidth="lg">
          {isLoadingBootstrap ? (
            <HeaderSkeleton />
          ) : (
            <>
              {mobileSearchOpen && !isDesktop ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => setMobileSearchOpen(false)}
                    sx={{ display: { xs: 'flex', lg: 'none' } }}
                    aria-label="Voltar"
                  >
                    <ArrowBack />
                  </IconButton>
                  <SearchInput />
                </Stack>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={{ xs: 1, md: 2 }}
                  >
                    <IconButton
                      onClick={() => setMobileOpen(true)}
                      sx={{ display: { xs: 'flex', lg: 'none' } }}
                      aria-label="Menu"
                    >
                      <DragHandle sx={{ color: 'primary.dark' }} />
                    </IconButton>
                    <MainLogo />
                    <Stack
                      direction="row"
                      sx={{ display: { xs: 'none', lg: 'flex' } }}
                    >
                      {!isLoadingBootstrap &&
                        visibleMenu.map((item) => {
                          const courses = item.label === 'Cursos';
                          const active =
                            path &&
                            (path === item.path ||
                              path.startsWith(item.path + '/'));
                          return (
                            <Button
                              key={item.label}
                              onClick={(e) => {
                                e.preventDefault();
                                onMenuItemClick(item);
                              }}
                              sx={({ palette }) => ({
                                color: active
                                  ? palette.primary.dark
                                  : palette.text.primary,
                                fontWeight: 'normal',
                              })}
                            >
                              {item.label}
                              {courses && (
                                <>
                                  {categoryMenuOpen ? (
                                    <ExpandLess fontSize="small" />
                                  ) : (
                                    <ExpandMore fontSize="small" />
                                  )}
                                </>
                              )}
                            </Button>
                          );
                        })}
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={{ md: 1 }}
                    alignItems="center"
                  >
                    <IconButton
                      sx={{ display: { xs: 'flex', lg: 'none' } }}
                      onClick={() => setMobileSearchOpen(true)}
                      aria-label="Buscar"
                    >
                      <Search />
                    </IconButton>
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                      <SearchInput />
                    </Box>
                    <UserMenu />
                  </Stack>
                </Stack>
              )}
              {categoryMenuOpen && (
                <CategoriesMenu
                  features={bootstrap?.plan?.features || []}
                  onClose={() => setCategoryMenuOpen(false)}
                />
              )}
            </>
          )}
        </Container>
      </StyledAppBar>
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        visibleMenu={visibleMenu}
        onMenuItemClick={handleMobileClick}
      />
    </>
  );
};
